import type { BeforeSendFn } from 'posthog-js'
import type { PostHog } from 'posthog-node'

import { posthogManager } from './manager'

export * from './authErrorCategory'
export * from './authErrorLoop'
export * from './phCookie'

type ErrorTracking = {
  error: Error | string | unknown
  properties?: Record<string, any>
  fingerprint: string
}

type SSRErrorTracking = ErrorTracking & {
  posthogInstance: PostHog
  userID?: string
}

// PostHog groups events by stack/name/message only when it gets an Error
// instance — passing a string lands them all in the "Non-Error captured"
// mega-bucket. Always coerce.
const toError = (error: unknown): Error => {
  if (error instanceof Error) return error
  if (typeof error === 'string') return new Error(error)
  try {
    return new Error(JSON.stringify(error))
  } catch {
    return new Error(String(error))
  }
}

// Splits broad fingerprints (e.g. 'apiError') into per-operation / per-status
// buckets so the dashboard shows the failing GraphQL op + HTTP status instead
// of one mega-issue. Callers that don't pass operation/status still get a
// stable but coarser fingerprint.
const composeFingerprint = (
  fingerprint: string,
  properties: Record<string, any>
) => {
  const operation = properties.operation ?? properties.operationName ?? '_'
  const status = properties.status ?? 'na'
  // Route/component scope (set by the generic ErrorBoundary) splits
  // otherwise-identical render crashes — e.g. a `removeChild` NotFoundError —
  // into per-screen buckets instead of one catch-all issue. Only appended
  // when present, so existing GraphQL (apiError/serverError) and SSR
  // fingerprints are byte-for-byte unchanged.
  const scope = [properties.route, properties.component]
    .filter(Boolean)
    .join(':')
  const base = `${fingerprint}:${operation}:${status}`
  return scope ? `${base}:${scope}` : base
}

// Browser auto-translation (Edge "Translate page", Chrome/Google Translate)
// rewrites text nodes under React and causes a class of `removeChild` /
// `insertBefore` NotFoundError crashes. Capturing the page language plus a
// translation heuristic on every exception makes those instantly identifiable
// instead of requiring an $os/$browser cross-reference after the fact. SSR-safe
// (returns {} when there is no DOM).
const collectTranslationContext = (): Record<string, unknown> => {
  if (typeof document === 'undefined' || typeof navigator === 'undefined') {
    return {}
  }
  const html = document.documentElement
  // Google/Edge translators add a `translated-ltr`/`translated-rtl` class to
  // <html> and wrap translated copy in <font> tags — either is a strong signal
  // the DOM was rewritten out from under React.
  const pageTranslated =
    /\btranslated-(?:ltr|rtl)\b/.test(html.className || '') ||
    document.getElementsByTagName('font').length > 0
  return {
    page_translated: pageTranslated,
    document_lang: html.lang || undefined,
    browser_language: navigator.language,
    browser_languages: Array.isArray(navigator.languages)
      ? navigator.languages.join(',')
      : undefined
  }
}

export const errorTracking = ({
  error,
  properties = {},
  fingerprint
}: ErrorTracking) => {
  const posthog = posthogManager.getClientInstance()
  if (!posthog) return

  const errorInstance = toError(error)

  posthog?.captureException(errorInstance, {
    $exception_fingerprint: composeFingerprint(fingerprint, properties),
    ...properties
  })
}
export const ssrErrorTracking = ({
  posthogInstance,
  userID,
  error,
  fingerprint,
  properties = {}
}: SSRErrorTracking) => {
  if (!posthogInstance) return

  const errorInstance = toError(error)

  posthogInstance?.captureException(errorInstance, userID, {
    $exception_fingerprint: composeFingerprint(fingerprint, properties),
    ...properties
  })
}

export const actionTracking = ({
  action,
  properties
}: {
  action: string
  properties?: Record<string, any>
}) => {
  const posthog = posthogManager.getClientInstance()
  if (!posthog) return

  posthog?.capture(action, {
    action,
    ...properties
  })
}

const errorsToIgnore: {
  type: string
  value: string
}[] = [
  // ref: https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/
  { type: 'UnhandledRejection', value: 'MethodName:update, ParamCount:4' },
  {
    type: 'UnhandledRejection',
    value: 'Failed to load https://www.googletagmanager.com'
  },
  // PostHog's own sourcemap fetcher hits .js.map 403s on the customer
  // subdomains and self-captures the failure. This is the 800-occurrence
  // "[object Object] from _app.tsx" super-issue.
  { type: 'UnhandledRejection', value: '.js.map' }
]

export const beforeSend: BeforeSendFn = event => {
  if (event.event === '$exception') {
    const exceptionList = event.properties['$exception_list'] || []
    const exception = exceptionList.length > 0 ? exceptionList[0] : null

    if (
      exception &&
      errorsToIgnore.some(
        error =>
          error.type === exception['type'] &&
          exception['value'].includes(error.value)
      )
    ) {
      return null // This prevents the event from being sent
    }

    // Enrich (don't drop) surviving exceptions with translation/locale context.
    Object.assign(event.properties, collectTranslationContext())
  }
  return event
}
